import { apiClient } from "@/lib/api";
import type {
  StandardResponse,
  PaginatedStandardResponse,
  PaginationInfo,
} from "@/types/api-response";
import { extractData, extractPaginatedData } from "@/types/api-response";
import type {
  PricingComponentListDto,
  PricingComponentDetailDto,
  CreatePricingComponentRequest,
  UpdatePricingComponentRequest,
  PricingComponentFilters,
} from "@/types/pricing-component";

/**
 * Admin Pricing Component Service
 *
 * Handles all pricing component management API calls for the admin panel.
 * All responses follow StandardResponse<T> or PaginatedStandardResponse<T> format.
 */
export const adminPricingComponentService = {
  /**
   * Get paginated list of pricing components with filters
   *
   * Returns paginated pricing components with pagination metadata.
   * Supports search, component type filtering, active status filtering, sorting, and pagination.
   *
   * @param filters - Filter parameters
   * @param filters.search - Optional search term (searches name, slug)
   * @param filters.componentType - Optional component type filter (Resource, Addon, Service, Usage)
   * @param filters.isActive - Optional active status filter (true/false/null for all)
   * @param filters.sortBy - Sort field (name, componentType, createdAt)
   * @param filters.sortOrder - Sort order (asc or desc)
   * @param filters.page - Page number (1-based, default: 1)
   * @param filters.limit - Items per page (default: 20, max: 100)
   */
  async getComponents(
    filters: PricingComponentFilters,
  ): Promise<{ data: PricingComponentListDto[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.componentType)
      params.append("componentType", filters.componentType);
    if (filters.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
    params.append("page", (filters.page ?? 1).toString());
    params.append("limit", (filters.limit ?? 20).toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<PricingComponentListDto>
    >(`/api/v1/admin/pricing/components?${params.toString()}`);

    // Interceptor already handles error responses, so response.data is guaranteed to be success
    return extractPaginatedData(response.data);
  },

  /**
   * Get detailed pricing component information by ID
   *
   * Returns complete pricing component details including price count.
   *
   * @param componentId - Pricing component ID (UUID)
   */
  async getComponentById(
    componentId: string,
  ): Promise<PricingComponentDetailDto> {
    const response = await apiClient.get<
      StandardResponse<PricingComponentDetailDto>
    >(`/api/v1/admin/pricing/components/${componentId}`);

    // Interceptor already handles error responses, so response.data is guaranteed to be success
    return extractData(response.data);
  },

  /**
   * Create new pricing component
   *
   * Returns created pricing component details with 201 Created status.
   *
   * @param data - Pricing component creation data
   */
  async createComponent(
    data: CreatePricingComponentRequest,
  ): Promise<PricingComponentDetailDto> {
    const response = await apiClient.post<
      StandardResponse<PricingComponentDetailDto>
    >("/api/v1/admin/pricing/components", data);

    // Interceptor already handles error responses, so response.data is guaranteed to be success
    return extractData(response.data);
  },

  /**
   * Update pricing component details
   *
   * Returns success message (200 OK).
   *
   * @param componentId - Pricing component ID (UUID)
   * @param data - Pricing component update data
   */
  async updateComponent(
    componentId: string,
    data: UpdatePricingComponentRequest,
  ): Promise<PricingComponentDetailDto> {
    const response = await apiClient.put<
      StandardResponse<PricingComponentDetailDto>
    >(`/api/v1/admin/pricing/components/${componentId}`, data);

    return extractData(response.data);
  },

  /**
   * Delete pricing component
   *
   * Returns success message (200 OK).
   * Note: This permanently deletes the pricing component.
   *
   * @param componentId - Pricing component ID (UUID)
   */
  async deleteComponent(componentId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/pricing/components/${componentId}`);

    // Interceptor already handles error responses
  },
};
